import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useOwnerAuth } from "./OwnerAuthContext";
import { ownerApi } from "./api";
import BannerHost from "@/components/BannerHost";
import { LayoutDashboard, Building2, Users, Settings, History, LogOut, KeyRound, ShieldCheck, Wrench, Palette, Bug } from "lucide-react";

const nav = [
  { to: "/owner/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/owner/organizations", icon: Building2, label: "Organizations" },
  { to: "/owner/users", icon: Users, label: "Owners" },
  { to: "/owner/platform-settings", icon: Settings, label: "Platform Settings" },
  { to: "/owner/themes", icon: Palette, label: "Themes" },
  { to: "/owner/audit-log", icon: History, label: "Audit Log" },
  { to: "/owner/dev-utility", icon: Wrench, label: "Dev Utility" },
];

export default function OwnerLayout() {
  const { owner, logout } = useOwnerAuth();
  const navigate = useNavigate();
  const [debugOn, setDebugOn] = useState(false);

  // Poll debug-mode flag every 30s so the banner reflects out-of-band toggles
  // (e.g., a second Owner flipping it from a different browser). Also listen
  // for an in-tab CustomEvent so the Dev Utility toggle reflects instantly.
  useEffect(() => {
    let cancelled = false;
    const fetchFlag = async () => {
      try {
        const { data } = await ownerApi.get("/debug-mode");
        if (!cancelled) setDebugOn(!!data.enabled);
      } catch { /* swallow — banner just stays hidden */ }
    };
    fetchFlag();
    const id = setInterval(fetchFlag, 30000);
    const onChange = (e) => setDebugOn(!!e?.detail?.enabled);
    window.addEventListener("owner:debug-mode-changed", onChange);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("owner:debug-mode-changed", onChange);
    };
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/owner/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#040b1d] text-[#ebeef5] font-body" data-testid="owner-shell">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#040b1d]/90 backdrop-blur border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center gap-6">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-md bg-[#10b7c0]/15 border border-[#10b7c0]/30 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-[#10b7c0]" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-heading font-bold text-sm tracking-wide">DiNAXLP</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#10b7c0] font-semibold">Platform</span>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1 ml-6 flex-1" data-testid="owner-nav">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide flex items-center gap-1.5 transition-colors ${
                    isActive
                      ? "bg-[#10b7c0]/10 text-[#10b7c0] border border-[#10b7c0]/30"
                      : "text-[#ebeef5]/70 hover:text-[#f1fcff] hover:bg-white/5 border border-transparent"
                  }`
                }
                data-testid={`owner-nav-${n.to.split("/").pop()}`}
              >
                <n.icon className="h-3.5 w-3.5" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <NavLink
              to="/owner/profile"
              className={({ isActive }) =>
                `hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                  isActive
                    ? "border-[#10b7c0]/30 bg-[#10b7c0]/10 text-[#10b7c0]"
                    : "border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff] hover:border-white/20"
                }`
              }
              title={owner?.email}
              data-testid="owner-profile-link"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {owner?.email}
            </NavLink>
            <button
              onClick={onLogout}
              className="text-xs font-semibold px-3 py-1.5 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff] hover:border-white/20 flex items-center gap-1.5 transition-colors"
              data-testid="owner-logout-btn"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 px-3 py-2 overflow-x-auto border-t border-white/5 scrollbar-thin">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `shrink-0 px-2.5 py-1 rounded text-[11px] font-semibold flex items-center gap-1 ${
                  isActive
                    ? "bg-[#10b7c0]/10 text-[#10b7c0] border border-[#10b7c0]/30"
                    : "text-[#ebeef5]/70 border border-transparent"
                }`
              }
            >
              <n.icon className="h-3 w-3" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>

      {debugOn && (
        <div
          className="bg-amber-400/15 border-b border-amber-400/40 text-amber-200"
          data-testid="owner-debug-mode-banner"
        >
          <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center gap-3 text-xs font-semibold">
            <Bug className="h-4 w-4 shrink-0 animate-pulse" />
            <span className="flex-1">
              <span className="uppercase tracking-wider mr-2">Debug Mode Active</span>
              <span className="font-normal text-amber-100/80">
                500 responses are returning full tracebacks. Disable before public traffic — internal paths and code structure are exposed to anyone who can trigger an error.
              </span>
            </span>
            <NavLink
              to="/owner/dev-utility"
              className="shrink-0 px-3 py-1 rounded border border-amber-300/60 hover:bg-amber-300/10 text-amber-200 transition-colors"
              data-testid="owner-debug-mode-banner-link"
            >
              Disable in Dev Utility →
            </NavLink>
          </div>
        </div>
      )}

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <BannerHost />
        <Outlet />
      </main>
    </div>
  );
}
